import { useCallback, useContext, useState } from 'react';
import { CheckoutContext } from '@/context/CheckoutContext';
import { PaymentMethodEnum, ProviderEnum } from '@/types/common';
import { AmazonPayStripeForm } from './AmazonPayStripeForm';
import { GenericPayForm } from './GenericPayForm';
import { InputTaxComponent } from '../InputTaxComponent/InputTaxComponent';

/**
 * Amazon Pay renderer entry point. Dispatches on the `preferExpressButton`
 * template feature flag (see {@link TemplateFeatures}) — leaves branch on
 * the flag, not on `template.type` directly, matching the
 * `deferExpressWalletReveal` convention.
 *
 *   - flag `true` (PREFERRED today) + STRIPE provider →
 *     {@link AmazonPayStripeForm} (ECE button) with `<InputTaxComponent>`
 *     mounted ABOVE it. The wallet button is the primary CTA, so the
 *     shopper fills the ZIP field first, then taps Pay. Mirrors
 *     {@link GooglePayForm}'s non-DEFAULT layout — without an inline
 *     ZIP field the shopper can't recover from a missing-tax error
 *     because `<PaymentMethodForm>`'s TaxInfoPrompt swap-in path only
 *     runs on DEFAULT (`blockExpressWalletsForZipCode`); PREFERRED opts
 *     out via `showBlockedZipCodeErrorInInput` and expects the input
 *     to render inline.
 *
 *   - otherwise (flag `false`, or non-Stripe provider) → legacy
 *     `<PaymentElement>` via {@link GenericPayForm}, which handles ZIP
 *     itself inside {@link StripeGenericPayForm}.
 *
 * Amazon Pay is Stripe-only today (see `amazonPayPaymentHandler`); the
 * provider check here is defensive so opt-in templates transparently
 * downgrade a non-Stripe Amazon Pay to the generic path instead of
 * forcing the ECE branch and failing loudly.
 */
export const AmazonPayForm = () => {
  const { paymentMethodsSettings, template, addressDetails, customer } = useContext(CheckoutContext);
  const [showZipCodeError, setShowZipCodeError] = useState(false);

  const providerSettings = paymentMethodsSettings.find((pm) => pm.paymentMethodName === PaymentMethodEnum.AMAZON_PAY);

  const shouldRenderAsExpress =
    providerSettings?.provider === ProviderEnum.STRIPE && template.features.preferExpressButton;

  const shouldShowZipCodeErrorInInput = template.features.showBlockedZipCodeErrorInInput;
  const zipCode = addressDetails?.zipCode ?? customer.address?.zipCode;
  const handleInvalidTaxClick = useCallback(() => {
    if (shouldShowZipCodeErrorInInput) {
      setShowZipCodeError(true);
    }
  }, [shouldShowZipCodeErrorInInput]);

  if (!shouldRenderAsExpress) {
    return <GenericPayForm paymentMethod={PaymentMethodEnum.AMAZON_PAY} />;
  }

  return (
    <div className="flex flex-col gap-4">
      <InputTaxComponent
        isBlockedForZipCode={showZipCodeError}
        onValidate={(isValid) => {
          if (isValid && zipCode?.trim()) setShowZipCodeError(false);
        }}
      />
      <AmazonPayStripeForm onInvalidTaxClick={shouldShowZipCodeErrorInInput ? handleInvalidTaxClick : undefined} />
    </div>
  );
};
